const AGENT_TOOL_NAMES = ['Agent', 'Task']
const HIDDEN_TOOL_NAMES = ['AskUserQuestion']

const BUILT_IN_TOOL_LABELS = {
  Bash: 'Run command',
  Edit: 'Edit file',
  Glob: 'Find files',
  Grep: 'Search files',
  Read: 'Read file',
  Skill: 'Use skill',
  TodoWrite: 'Update plan',
  WebFetch: 'Open web page',
  WebSearch: 'Search the web',
  Write: 'Write file'
}

export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

export const prettyToolName = (toolName) => {
  const name = String(toolName || 'tool')
  if (BUILT_IN_TOOL_LABELS[name]) return BUILT_IN_TOOL_LABELS[name]
  const withoutServer = name.replace(/^mcp__.+?__/, '')
  const withoutPrefix = withoutServer.replace(/^(core|jobs|integrations|statemachines|temp_files)_/, '')
  const words = withoutPrefix.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const formatDuration = (ms) => {
  const seconds = Math.max(0, Math.round((Number(ms) || 0) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
}

export const isAgentTool = (toolName) => AGENT_TOOL_NAMES.includes(toolName)

export const isHiddenTool = (toolName) => HIDDEN_TOOL_NAMES.includes(toolName)

export const createActivity = (id, startedAt = Date.now()) => ({
  collapsed: false,
  finishedAt: null,
  id,
  nodes: {},
  rootIds: [],
  startedAt,
  status: 'running'
})

export const createToolNode = (event) => ({
  agentText: '',
  children: [],
  detail: event.detail || '',
  elapsedSeconds: 0,
  isAgent: isAgentTool(event.toolName),
  lastToolName: null,
  parentToolUseId: event.parentToolUseId || null,
  status: 'running',
  subagentType: event.subagentType || null,
  summary: '',
  toolName: event.toolName,
  toolUseId: event.toolUseId,
  usage: null
})

export const applyToolEvent = (activity, event) => {
  if (!event?.toolUseId || isHiddenTool(event.toolName)) return activity
  let node = activity.nodes[event.toolUseId]

  if (!node) {
    node = createToolNode(event)
    activity.nodes[event.toolUseId] = node
    const parent = activity.nodes[node.parentToolUseId]
    if (parent) parent.children.push(node.toolUseId)
    else activity.rootIds.push(node.toolUseId)
  }

  if (event.phase === 'progress') node.elapsedSeconds = event.elapsedSeconds || node.elapsedSeconds
  if (event.phase === 'end') {
    node.status = event.status === 'error' ? 'error' : 'success'
    node.summary = event.summary || node.summary
  }
  return activity
}

export const applyTaskEvent = (activity, event) => {
  const node = activity.nodes[event?.toolUseId]
  if (!node) return activity
  node.isAgent = true
  if (event.subagentType) node.subagentType = event.subagentType
  if (event.lastToolName) node.lastToolName = event.lastToolName
  if (event.usage) node.usage = event.usage
  if (event.phase === 'progress' && event.summary) node.agentText = event.summary
  if (event.phase === 'notification') {
    node.status = event.status === 'completed' ? 'success' : 'error'
    node.summary = event.summary || node.summary
  }
  return activity
}

export const applyAgentText = (activity, event) => {
  const node = activity.nodes[event?.parentToolUseId]
  if (node) node.agentText = event.text || node.agentText
  return activity
}

export const finishActivity = (activity, status, finishedAt = Date.now()) => {
  activity.status = status
  activity.finishedAt = finishedAt
  for (const node of Object.values(activity.nodes)) {
    if (node.status === 'running') node.status = status === 'success' ? 'success' : 'stopped'
  }
  if (status === 'success') activity.collapsed = true
  return activity
}

export const countSteps = (activity) => Object.values(activity.nodes).length

const renderStatusIcon = (status) => `<span class="cg-icon cg-icon--${escapeHtml(status)}"></span>`

const renderNodeMeta = (node) => {
  const parts = []
  if (node.isAgent && node.usage?.tool_uses) parts.push(`${node.usage.tool_uses} tool uses`)
  if (node.isAgent && node.usage?.duration_ms) parts.push(formatDuration(node.usage.duration_ms))
  if (!node.isAgent && node.status === 'running' && node.elapsedSeconds) parts.push(`${node.elapsedSeconds}s`)
  if (!parts.length) return ''
  return `<span class="cg-step-meta">${escapeHtml(parts.join(' · '))}</span>`
}

const renderToolNode = (activity, node) => {
  const detail = node.status === 'running' || !node.summary ? node.detail : node.summary
  return `<div class="cg-step cg-step--${escapeHtml(node.status)}">${renderStatusIcon(node.status)}<div class="cg-step-body"><div class="cg-step-title">${escapeHtml(prettyToolName(node.toolName))}${renderNodeMeta(node)}</div>${detail ? `<div class="cg-step-detail">${escapeHtml(detail)}</div>` : ''}</div></div>`
}

const renderAgentNode = (activity, node) => {
  const children = node.children.map(id => renderNode(activity, activity.nodes[id])).join('')
  const thought = node.status === 'running' && node.agentText ? `<div class="cg-agent-thought">${escapeHtml(node.agentText)}</div>` : ''
  const result = node.status !== 'running' && node.summary ? `<div class="cg-agent-result">${escapeHtml(node.summary)}</div>` : ''
  const type = node.subagentType ? `<span class="cg-agent-type">${escapeHtml(node.subagentType)}</span>` : ''
  return `<div class="cg-agent cg-agent--${escapeHtml(node.status)}"><div class="cg-agent-head">${renderStatusIcon(node.status)}<span class="cg-agent-label">Subagent</span>${type}${renderNodeMeta(node)}</div><div class="cg-agent-title">${escapeHtml(node.detail || 'Working on a subtask')}</div>${children ? `<div class="cg-agent-steps">${children}</div>` : ''}${thought}${result}</div>`
}

const renderNode = (activity, node) => {
  if (!node) return ''
  return node.isAgent ? renderAgentNode(activity, node) : renderToolNode(activity, node)
}

const renderActivityTitle = (activity, now) => {
  const steps = countSteps(activity)
  const elapsed = formatDuration((activity.finishedAt || now) - activity.startedAt)
  const stepLabel = steps === 1 ? '1 step' : `${steps} steps`
  if (activity.status === 'running' && !steps) return `<span class="cg-shimmer">Thinking</span><span class="cg-activity-meta">${elapsed}</span>`
  if (activity.status === 'running') return `<span class="cg-shimmer">Working</span><span class="cg-activity-meta">${stepLabel} · ${elapsed}</span>`
  if (activity.status === 'stopped') return `<span>Stopped</span><span class="cg-activity-meta">${steps ? `${stepLabel} · ` : ''}${elapsed}</span>`
  if (activity.status === 'error') return `<span>Something went wrong</span><span class="cg-activity-meta">${steps ? `${stepLabel} · ` : ''}${elapsed}</span>`
  if (!steps) return `<span>Thought for ${elapsed}</span>`
  return `<span>Worked for ${elapsed}</span><span class="cg-activity-meta">${stepLabel}</span>`
}

export const renderActivityHtml = (activity, now = Date.now()) => {
  const steps = activity.rootIds.map(id => renderNode(activity, activity.nodes[id])).join('')
  const open = activity.collapsed ? '' : ' open'
  const icon = activity.status === 'running' ? renderStatusIcon('running') : renderStatusIcon(activity.status === 'success' ? 'done' : activity.status)
  const chevron = steps ? '<span class="cg-chevron"></span>' : ''
  return `<details class="cg-activity cg-activity--${escapeHtml(activity.status)}${steps ? '' : ' cg-activity--empty'}" data-activity-id="${escapeHtml(activity.id)}"${open}><summary class="cg-activity-toggle">${icon}<span class="cg-activity-title">${renderActivityTitle(activity, now)}</span>${chevron}</summary>${steps ? `<div class="cg-steps">${steps}</div>` : ''}</details>`
}

export const createQuestionCard = (event) => ({
  answers: null,
  questions: Array.isArray(event.questions) ? event.questions : [],
  selections: {},
  status: 'pending',
  toolUseId: event.toolUseId
})

export const needsSubmitButton = (card) => card.questions.length > 1 || card.questions.some(q => q.multiSelect)

export const toggleQuestionOption = (card, questionIndex, label) => {
  const question = card.questions[questionIndex]
  if (!question || card.status !== 'pending') return card
  const current = card.selections[questionIndex] || []
  const isSelected = current.includes(label)
  if (question.multiSelect) card.selections = { ...card.selections, [questionIndex]: isSelected ? current.filter(l => l !== label) : [...current, label] }
  else card.selections = { ...card.selections, [questionIndex]: [label] }
  return card
}

export const isQuestionCardComplete = (card) => card.questions.every((q, i) => (card.selections[i] || []).length > 0)

export const buildQuestionAnswers = (card) => card.questions.reduce((acc, q, i) => {
  acc[q.question] = (card.selections[i] || []).join(', ')
  return acc
}, {})

const renderQuestionOption = (card, questionIndex, option) => {
  const answer = card.answers?.[card.questions[questionIndex].question] || ''
  const selected = card.status === 'pending'
    ? (card.selections[questionIndex] || []).includes(option.label)
    : answer.split(', ').includes(option.label)
  const disabled = card.status !== 'pending' ? ' disabled' : ''
  return `<button class="cg-q-option${selected ? ' cg-q-option--selected' : ''}" data-q-index="${questionIndex}" data-q-label="${escapeHtml(option.label)}"${disabled}><span class="cg-q-check"></span><span class="cg-q-option-text"><span class="cg-q-option-label">${escapeHtml(option.label)}</span>${option.description ? `<span class="cg-q-option-desc">${escapeHtml(option.description)}</span>` : ''}</span></button>`
}

const renderQuestion = (card, question, index) => {
  const answer = card.answers?.[question.question]
  const labels = (question.options || []).map(o => o.label)
  const customAnswer = answer && !answer.split(', ').every(a => labels.includes(a)) ? `<div class="cg-q-custom">Answered: ${escapeHtml(answer)}</div>` : ''
  const header = question.header ? `<span class="cg-q-header">${escapeHtml(question.header)}</span>` : ''
  const hint = question.multiSelect ? '<span class="cg-q-hint">Select all that apply</span>' : ''
  return `<div class="cg-q-block">${header}${hint}<div class="cg-q-text">${escapeHtml(question.question)}</div><div class="cg-q-options">${(question.options || []).map(o => renderQuestionOption(card, index, o)).join('')}</div>${customAnswer}</div>`
}

const renderQuestionFooter = (card) => {
  if (card.status === 'answered') return '<div class="cg-q-footer"><span class="cg-q-status">Answer sent</span></div>'
  if (card.status === 'cancelled') return '<div class="cg-q-footer"><span class="cg-q-status">No longer waiting for an answer</span></div>'
  const submit = needsSubmitButton(card) ? `<button class="cg-q-submit"${isQuestionCardComplete(card) ? '' : ' disabled'}>Send answer</button>` : ''
  return `<div class="cg-q-footer"><span class="cg-q-status">Or type your own answer below</span>${submit}</div>`
}

export const renderQuestionHtml = (card) => {
  const questions = card.questions.map((q, i) => renderQuestion(card, q, i)).join('')
  return `<div class="cg-question cg-question--${escapeHtml(card.status)}" data-tool-use-id="${escapeHtml(card.toolUseId)}"><div class="cg-q-title"><span class="cg-q-badge"></span>The agent needs your input</div>${questions}${renderQuestionFooter(card)}</div>`
}

export const parseAgentEvent = (content) => {
  if (!content) return null
  if (typeof content === 'object') return content
  if (typeof content !== 'string' || !content.trim().startsWith('{')) return null
  try {
    return JSON.parse(content)
  } catch (err) {
    return null
  }
}
